// [S-707 / E-PERSONA-ADMIN] Persona configuration — list view.
//
// Replaces the old idea of Studio Agents as a per-persona test-chat surface
// with what it actually needs to be once Home routes to every persona: a
// configuration/administration console. Lists the 5 fixed personas defined
// in agentRegistry.ts (a code-enumerated set, never paginated — there will
// never be more than a handful), each with its draft/published status and
// whether it's bound to a server-held workflow (LongFormWorkflowService /
// ClauseReviewService), whose steps live in the persona's own detail page
// (WorkflowStepView) rather than a separate page.
import { Link } from "react-router-dom";
import { Alert, LoadingState, PageHead, Pill } from "../components";
import { getApiErrorMessage } from "../data/api";
import { usePersonaConfigs } from "../data/personaConfigQuery";

export function PersonaManagement(): JSX.Element {
  const personas = usePersonaConfigs();

  return (
    <div className="page pa">
      <PageHead
        eyebrow="Agent Personas"
        title="Persona configuration"
        sub="Edit each chat persona's instructions, tools, model, and limits without writing code. Changes land as a draft — publish to make them live."
      />

      {personas.isLoading && <LoadingState label="Loading personas…" />}
      {personas.isError && (
        <Alert label="Couldn't load personas" tone="error">
          {getApiErrorMessage(personas.error) ?? "Unknown error"}
        </Alert>
      )}

      {personas.data !== undefined && (
        <div className="pa__list" role="list">
          {personas.data.personas.map((p) => (
            <Link key={p.kind} to={`/admin/personas/${p.kind}`} className="pa__row" role="listitem">
              <div className="pa__row-main">
                <div className="pa__row-title">
                  {p.label}
                  {p.workflowBound && (
                    <span className="pa__row-workflow" title="Bound to a server-held workflow">
                      Workflow
                    </span>
                  )}
                </div>
                <div className="pa__row-blurb">{p.blurb}</div>
              </div>
              <div className="pa__row-status">{statusPill(p)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function statusPill(p: {
  hasDraft: boolean;
  hasPublished: boolean;
}): JSX.Element {
  if (p.hasDraft) return <Pill variant="medium">Draft pending</Pill>;
  if (p.hasPublished) return <Pill variant="high">Published override</Pill>;
  return <Pill variant="low">Default</Pill>;
}
